import * as React from 'react'

type UseIsInViewportOptions = IntersectionObserverInit & {
  once?: boolean
}

export function useIsInViewport(
  ref?: React.RefObject<Element | null>,
  initial = false,
  options?: UseIsInViewportOptions,
) {
  const [isIntersecting, setIsIntersecting] = React.useState(initial)

  const root = options?.root ?? null
  const rootMargin = options?.rootMargin ?? '0px'
  const threshold = options?.threshold ?? 0
  const once = options?.once ?? false

  const onceRef = React.useRef(once)
  onceRef.current = once

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    let raf = 0
    let observer: IntersectionObserver | null = null
    let observedEl: Element | null = null
    let didTriggerOnce = false

    const start = () => {
      const el = ref?.current
      if (!el) {
        // ref not ready yet; try next frame
        raf = requestAnimationFrame(start)
        return
      }

      observedEl = el
      observer = new IntersectionObserver(
        entries => {
          const entry = entries.at(-1)
          if (!entry) {
            return
          }

          if (onceRef.current) {
            if (!didTriggerOnce && entry.isIntersecting) {
              didTriggerOnce = true
              setIsIntersecting(true)
              observer?.unobserve(el)
              observer?.disconnect()
            }
            return
          }

          setIsIntersecting(entry.isIntersecting)
        },
        { root, rootMargin, threshold },
      )

      observer.observe(el)
    }

    start()

    return () => {
      if (raf) {
        cancelAnimationFrame(raf)
      }
      if (observer && observedEl) {
        observer.unobserve(observedEl)
      }
      observer?.disconnect()
    }
  }, [ref, root, rootMargin, threshold])

  return isIntersecting
}
